import uuid from "uuid-random";
import { OptSilentError } from "../core/types";
import {
  checkExhausted,
  filterAndMap,
  isArrayOf,
  isString,
} from "../core/utils";
import {
  DatasetConfig,
  Filter,
  initStandardFilter,
  initRadioFilter,
  FilterConfigType,
} from "./config";
import { Article, ArticleDataValue, valueArray } from "./types";

interface Column {
  heading: string;
  replaceValue?: string | null;
}

export function sanitize(raw: string): string {
  return raw.replace(/ +/g, " ").trim();
}

function splitColumns(rawData: string[][]): [Array<Column>, string[][]] {
  return [
    rawData[0].reduce(
      (acc, heading, i) => [
        ...acc,
        {
          heading:
            sanitize(heading) === "&"
              ? acc[acc.length - 1].heading
              : sanitize(heading),
          replaceValue:
            sanitize(rawData[1][i]).length > 0 ? sanitize(rawData[1][i]) : null,
        },
      ],
      [] as Array<Column>
    ),
    rawData.slice(2),
  ];
}

export function parseRawTable(
  rawData: string[][]
): Record<string, ArticleDataValue>[] {
  const [columns, data] = splitColumns(rawData);
  return data
    .map((row) =>
      columns.reduce(
        (acc: Record<string, ArticleDataValue>, column, i) =>
          i >= row.length || row[i].length === 0
            ? acc
            : {
                ...acc,
                [column.heading]:
                  acc[column.heading] != null
                    ? [
                        ...valueArray(acc[column.heading]),
                        column.replaceValue ?? sanitize(row[i]),
                      ]
                    : column.replaceValue ?? sanitize(row[i]),
              },
        {}
      )
    )
    .filter((row) =>
      Object.values(row).some((item) => item != null && item.length > 0)
    );
}

export function dataToArticles(
  articleData: Record<string, ArticleDataValue>[],
  config: DatasetConfig,
  rawTextLines: Array<string>,
  generateId: (
    data: Record<string, ArticleDataValue>,
    i: number
  ) => string = uuid
): OptSilentError<Array<Article>> {
  const articles: Array<Article> = [];
  for (let i = 0; i < articleData.length; i++) {
    const data = articleData[i];
    const singleValues = [
      data[config.longitude],
      data[config.latitude],
      data[config.shortDescription] ?? "",
    ];
    if (isArrayOf(isString)(singleValues)) {
      articles.push({
        id: generateId(data, i),
        data,
        position: [Number(singleValues[0]), Number(singleValues[1])],
        shortDescription: singleValues[2],
        raw: rawTextLines[i],
      });
    } else {
      const columnNames = ["Longitude", "Latitude", "Short Description"];

      const nullColumns = filterAndMap(singleValues, (value, i) =>
        value == null ? columnNames[i] : null
      );
      const multiValueColumns = filterAndMap(singleValues, (value, i) =>
        Array.isArray(value) ? columnNames[i] : null
      );

      if (nullColumns.length > 0) {
        return [
          null,
          new Error(`Value(s) for ${nullColumns.join(", ")} not found.`),
        ];
      } else {
        return [
          null,
          new Error(
            `Unexpected multiple columns for ${multiValueColumns.join(", ")}`
          ),
        ];
      }
    }
  }
  return [articles, null];
}

export function initFilter(
  articleData: Record<string, ArticleDataValue>[],
  column: string,
  type: FilterConfigType
): OptSilentError<Filter> {
  const options = articleData
    .reduce(
      (acc: Array<string>, data) =>
        data[column] == null
          ? acc
          : acc.concat(
              valueArray(data[column]).filter((val) => !acc.includes(val))
            ),
      []
    )
    .sort();

  if (options.length > 0) {
    switch (type) {
      case "Standard":
        return [initStandardFilter(column, options), null];
      case "Radio_on":
        return [initRadioFilter(true, column, options), null];
      case "Radio_off":
        return [initRadioFilter(false, column, options), null];
      default:
        return checkExhausted(type);
    }
  } else {
    return [
      null,
      new Error(
        `Options for filter: "${column}" not found. Make sure capitalization is the same, and there is at least one value in the column.`
      ),
    ];
  }
}

export function initFilters(
  articleData: Record<string, ArticleDataValue>[],
  filterConfig: Record<string, FilterConfigType>
): OptSilentError<Array<Filter>> {
  const filters: Array<Filter> = [];

  for (const column in filterConfig) {
    const [filter, error] = initFilter(
      articleData,
      sanitize(column),
      filterConfig[column]
    );
    if (error != null) {
      return [null, error];
    } else {
      filters.push(filter);
    }
  }

  return [filters, null];
}
